import { useEffect, useRef } from 'react'
import confetti from 'canvas-confetti'
import { useTaskStore } from '@/stores/taskStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useProfileStore } from '@/stores/profileStore'

/**
 * canvas-confetti의 `colors`는 HEX만 허용한다. 디자인 토큰(--color-*)은 oklch로
 * 정의돼 있어 그대로 넘기면 파티클이 깨진다. 발사 시점에 getComputedStyle로 토큰을
 * 해석한 뒤 canvas에 칠해 정규화된 HEX로 변환한다. 이렇게 하면 활성 팔레트/다크모드가
 * 반영되며, 토큰을 못 읽으면 기존 HEX fallback으로 동작이 유지된다.
 */
function resolveTokenHex(varName: string, fallbackHex: string): string {
  if (typeof window === 'undefined' || typeof document === 'undefined') return fallbackHex
  const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
  if (!raw) return fallbackHex
  try {
    const ctx = document.createElement('canvas').getContext('2d')
    if (!ctx) return fallbackHex
    // 유효하지 않은 값이면 fillStyle 할당이 무시되므로 기준값을 먼저 둔다.
    ctx.fillStyle = '#000000'
    ctx.fillStyle = raw // 브라우저가 oklch()/var 해석값을 정규화
    const norm = ctx.fillStyle // '#rrggbb' 또는 'rgb(...)'
    if (norm.startsWith('#')) return norm
    const m = norm.match(/\d+/g)
    if (!m || m.length < 3) return fallbackHex
    const [r, g, b] = m.map(Number)
    return '#' + [r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')
  } catch {
    return fallbackHex
  }
}

/**
 * Fire confetti when the user hits their daily goal.
 * Watches completed-today count vs dailyGoal setting.
 */
export function useGoalConfetti() {
  const tasks = useTaskStore((s) => s.tasks)
  const dailyGoal = useSettingsStore((s) => s.settings.dailyGoal)
  const prevCountRef = useRef(0)

  useEffect(() => {
    if (!dailyGoal || dailyGoal <= 0) return

    // Local-date comparison — completedAt is stored as UTC ISO; compare in user's tz.
    const fmt = (iso: string) => {
      const d = new Date(iso)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    const completedTasksToday = tasks.filter(
      (t) => t.status === 'completed' && !!t.completedAt && fmt(t.completedAt) === today,
    ).length
    const completedSubtasksToday = tasks.reduce((acc, t) =>
      acc + t.subtasks.filter(st => st.isCompleted && !!st.completedAt && fmt(st.completedAt) === today).length,
      0
    )
    const completedToday = completedTasksToday + completedSubtasksToday

    // Fire confetti only at the exact moment the goal is reached
    if (completedToday >= dailyGoal && prevCountRef.current < dailyGoal) {
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      if (!prefersReducedMotion) {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.7 },
          // 활성 팔레트/다크모드를 반영하도록 발사 시점에 토큰을 HEX로 해석
          colors: [
            resolveTokenHex('--color-primary-400', '#a78bfa'),
            resolveTokenHex('--color-primary-300', '#60a5fa'),
            resolveTokenHex('--color-success-500', '#34d399'),
            resolveTokenHex('--color-warning-500', '#fbbf24'),
            resolveTokenHex('--color-primary-200', '#f472b6'),
          ],
        })
      }
    }
    prevCountRef.current = completedToday
  }, [tasks, dailyGoal])
}

const STREAK_MILESTONES = [3, 7, 14, 30, 50, 100]

/**
 * Fire special confetti when the user reaches streak milestones.
 */
export function useStreakConfetti() {
  const currentStreak = useProfileStore((s) => s.stats.currentStreak)
  const prevStreakRef = useRef(0)

  useEffect(() => {
    if (currentStreak <= prevStreakRef.current) {
      prevStreakRef.current = currentStreak
      return
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    if (prefersReducedMotion) {
      prevStreakRef.current = currentStreak
      return
    }

    const timers: ReturnType<typeof setTimeout>[] = []

    if (STREAK_MILESTONES.includes(currentStreak)) {
      // Bigger celebration for bigger milestones
      const intensity = currentStreak >= 30 ? 3 : currentStreak >= 14 ? 2 : 1

      for (let i = 0; i < intensity; i++) {
        timers.push(setTimeout(() => {
          confetti({
            particleCount: 60 + i * 30,
            spread: 80 + i * 20,
            origin: { y: 0.6, x: 0.3 + i * 0.2 },
            // 활성 팔레트/다크모드를 반영하도록 발사 시점에 토큰을 HEX로 해석
            colors: [
              resolveTokenHex('--color-warning-500', '#fbbf24'),
              resolveTokenHex('--color-warning-600', '#f59e0b'),
              resolveTokenHex('--color-primary-600', '#d97706'),
              resolveTokenHex('--color-primary-400', '#a78bfa'),
              resolveTokenHex('--color-primary-300', '#60a5fa'),
            ],
          })
        }, i * 300))
      }
    }

    prevStreakRef.current = currentStreak

    return () => {
      timers.forEach(clearTimeout)
    }
  }, [currentStreak])
}
